import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import {
  Copy,
  Check,
  X,
  ArrowRight,
  ArrowRightLeft,
  Download,
  Share,
  Table,
  Atom,
  Pencil,
  Trash2,
} from "lucide-react";
import {
  getMoleculeSVG,
  getMoleculeRender,
  createPublicTextShare,
} from "../../../utils/Api";
import { useDownloadPng } from "../../../hooks/useDownloadPng";
import { inferMetadataFromContext } from "../../../utils/transformData";
import { sanitizeTableHtml } from "../../../utils/sanitizeTableHtml";

// ─── Helpers ────────────────────────────────────────────────────────────────

const truncateSmiles = (smiles: string, maxLen: number = 24): string => {
  if (!smiles) return "";
  return smiles.length > maxLen ? smiles.substring(0, maxLen) + "..." : smiles;
};


/** Format coref text into a labelled metadata element. Label portion is faint italic. */
const formatMetadata = (
  text: string | null | undefined,
): React.ReactNode | null => {
  if (!text) return null;
  const trimmed = text.trim();
  if (!trimmed) return null;

  // Numbers followed by % → Yield
  if (/\d+\.?\d*\s*%/.test(trimmed)) {
    return trimmed;
  }

  // A single letter, or numbers optionally followed by a single letter → Coreference
  if (/^\d+[a-zA-Z]?$/.test(trimmed) || /^[a-zA-Z]$/.test(trimmed)) {
    return (
      <>
        <span className="text-slate-400 italic">Coreference: </span>
        {trimmed}
      </>
    );
  }

  // Anything else → plain text
  return trimmed;
};

/**
 * Spatially sort items that have a bbox [x1, y1, x2, y2].
 *
 * 1. Group items into "rows" – two items belong to the same row when their
 *    vertical (y) extents overlap by more than 50 % of the *shorter* item's
 *    height.
 * 2. Sort rows top-to-bottom (by minimum y1 in the row).
 * 3. Within each row, sort items left-to-right by x1.
 *
 * Items that share a pageIndex are sorted independently per page (page order
 * is already guaranteed by the caller).
 */
function spatialSort<T extends { bbox?: number[]; pageIndex: number }>(
  items: T[],
): T[] {
  if (items.length <= 1) return items;

  // Partition by page so spatial sort is per-page
  const byPage = new Map<number, T[]>();
  items.forEach((item) => {
    const list = byPage.get(item.pageIndex) || [];
    list.push(item);
    byPage.set(item.pageIndex, list);
  });

  const sorted: T[] = [];

  // Helper – safely read bbox coordinate (fallback 0)
  const bx = (item: T, i: number): number => {
    const b = item.bbox;
    if (!b || i >= b.length) return 0;
    return b[i] ?? 0;
  };

  // Process pages in ascending order
  Array.from(byPage.keys())
    .sort((a, b) => a - b)
    .forEach((pageIdx) => {
      const pageItems = byPage.get(pageIdx)!;
      // Items without bbox go to the end
      const withBbox: T[] = pageItems.filter(
        (i) => i.bbox && i.bbox.length >= 4,
      );
      const withoutBbox: T[] = pageItems.filter(
        (i) => !i.bbox || i.bbox.length < 4,
      );

      // Build rows by clustering items with >50 % vertical overlap
      const assigned = new Set<number>();
      const rows: T[][] = [];

      // Sort candidates by y1 first so we sweep top-to-bottom
      const candidates: T[] = withBbox
        .slice()
        .sort((a, b) => bx(a, 1) - bx(b, 1));

      for (let idx = 0; idx < candidates.length; idx++) {
        if (assigned.has(idx)) continue;
        const item = candidates[idx] as T;

        const row: T[] = [item];
        assigned.add(idx);

        // Row vertical extent (grows as we add items)
        let rowY1: number = bx(item, 1);
        let rowY2: number = bx(item, 3);

        // Try to add subsequent items to this row
        for (let j = idx + 1; j < candidates.length; j++) {
          if (assigned.has(j)) continue;
          const other = candidates[j] as T;
          const otherY1: number = bx(other, 1);
          const otherY2: number = bx(other, 3);

          // Compute vertical overlap between `other` and the current row extent
          const overlapTop: number = Math.max(rowY1, otherY1);
          const overlapBot: number = Math.min(rowY2, otherY2);
          const overlap: number = Math.max(0, overlapBot - overlapTop);

          const otherHeight: number = otherY2 - otherY1;
          const rowHeight: number = rowY2 - rowY1;
          const minHeight: number = Math.min(otherHeight, rowHeight);

          if (minHeight > 0 && overlap / minHeight > 0.5) {
            row.push(other);
            assigned.add(j);
            // Expand row extent
            rowY1 = Math.min(rowY1, otherY1);
            rowY2 = Math.max(rowY2, otherY2);
          }
        }

        rows.push(row);
      }

      // Sort rows by their minimum y1
      rows.sort((a, b) => {
        const aMinY = Math.min(...a.map((i) => bx(i, 1)));
        const bMinY = Math.min(...b.map((i) => bx(i, 1)));
        return aMinY - bMinY;
      });

      // Within each row, sort by x1
      rows.forEach((row) => {
        row.sort((a, b) => bx(a, 0) - bx(b, 0));
        sorted.push(...row);
      });

      sorted.push(...withoutBbox);
    });

  return sorted;
}

// ─── Inline molecule image (prefers DRKit graph render, falls back to CDK Depict) ─

const InlineMoleculeImage: React.FC<{
  smiles: string;
  graph?:
    | { symbols: string[]; coords: number[][]; edges: number[][] }
    | null
    | undefined;
  bboxWidth?: number | undefined;
  bboxHeight?: number | undefined;
  className?: string;
}> = ({
  smiles,
  graph,
  bboxWidth = 1,
  bboxHeight = 1,
  className = "w-20 h-16",
}) => {
  const [imageUrl, setImageUrl] = useState<string>("");
  const [error, setError] = useState(false);
  const [inView, setInView] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Only start the network request once the element is near the viewport
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ob = new IntersectionObserver(
      ([entry]) => {
        if (entry!.isIntersecting) {
          setInView(true);
          ob.disconnect();
        }
      },
      { rootMargin: "200px" },
    );
    ob.observe(el);
    return () => ob.disconnect();
  }, []);

  useEffect(() => {
    if (!inView) return;
    let revoke = "";
    setImageUrl("");
    setError(false);
    (async () => {
      try {
        let blob: Blob;
        if (graph?.symbols?.length) {
          const response = await getMoleculeRender({
            symbols: graph.symbols,
            coords: graph.coords,
            edges: graph.edges,
            bbox_width: bboxWidth,
            bbox_height: bboxHeight,
          });
          blob = response.data as Blob;
        } else if (smiles) {
          const response = await getMoleculeSVG(smiles);
          blob = new Blob([response.data], { type: "image/svg+xml" });
        } else {
          return;
        }
        const url = URL.createObjectURL(blob);
        revoke = url;
        setImageUrl(url);
      } catch {
        setError(true);
      }
    })();
    return () => {
      if (revoke) URL.revokeObjectURL(revoke);
    };
  }, [inView, smiles, graph, bboxWidth, bboxHeight]);

  if (error || (!graph?.symbols?.length && !smiles)) {
    return (
      <div
        ref={containerRef}
        className={`${className} bg-slate-100 rounded flex items-center justify-center`}
      >
        <span className="text-[10px] text-slate-400 font-mono break-all text-center px-1">
          {smiles ? truncateSmiles(smiles, 16) : "—"}
        </span>
      </div>
    );
  }

  if (!imageUrl) {
    return (
      <div
        ref={containerRef}
        className={`${className} bg-slate-50 animate-pulse rounded`}
      />
    );
  }

  return (
    <div
      ref={containerRef}
      className={`${className} flex items-center justify-center`}
    >
      <img
        src={imageUrl}
        alt="molecule"
        className="w-full h-full object-contain"
      />
    </div>
  );
};

// ─── SMILES cell with truncation + copy ─────────────────────────────────────

const SmilesCell: React.FC<{ smiles: string; maxLen?: number }> = ({
  smiles,
  maxLen = 16,
}) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (smiles) {
      await navigator.clipboard.writeText(smiles);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }
  };

  return (
    <div className="flex items-center gap-1 min-w-0">
      <span
        className="text-xs font-mono text-slate-700 truncate min-w-0"
        title={smiles}
      >
        {truncateSmiles(smiles, maxLen)}
      </span>
      {smiles && (
        <button
          onClick={handleCopy}
          className="p-0.5 hover:bg-slate-200 rounded transition-colors flex-shrink-0"
          title="Copy SMILES"
        >
          {copied ? (
            <Check size={12} className="text-green-600" />
          ) : (
            <Copy size={12} className="text-slate-400" />
          )}
        </button>
      )}
    </div>
  );
};

// ─── Molecule stack (used in reaction rows for reactants / products) ────────

const MoleculeStack: React.FC<{
  molecules: {
    smiles: string;
    uuid?: string;
    graph?:
      | { symbols: string[]; coords: number[][]; edges: number[][] }
      | null
      | undefined;
    bboxWidth?: number | undefined;
    bboxHeight?: number | undefined;
  }[];
}> = ({ molecules }) => {
  if (!molecules || molecules.length === 0) {
    return <span className="text-xs text-slate-400 italic">None</span>;
  }

  return (
    <div className="flex flex-col gap-2 min-w-0">
      {molecules.map((mol, idx) => (
        <div key={mol.uuid || idx} className="flex items-center gap-2 min-w-0">
          <InlineMoleculeImage
            smiles={mol.smiles}
            graph={mol.graph}
            bboxWidth={mol.bboxWidth}
            bboxHeight={mol.bboxHeight}
            className="w-16 h-12 shrink-0"
          />
        </div>
      ))}
    </div>
  );
};

// ─── Molecule detail modal ──────────────────────────────────────────────────

const MoleculeDetailModal: React.FC<{
  smiles: string;
  coref?: string | null;
  graph?:
    | { symbols: string[]; coords: number[][]; edges: number[][] }
    | null
    | undefined;
  bboxWidth?: number | undefined;
  bboxHeight?: number | undefined;
  onClose: () => void;
}> = ({ smiles, coref, graph, bboxWidth, bboxHeight, onClose }) => {
  const { containerRef, download } = useDownloadPng("molecule.png");

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-2xl max-w-lg w-full mx-4 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-100 bg-white">
          <h3 className="text-sm font-semibold text-gray-900">
            Molecule Detail
          </h3>
          <div className="flex items-center gap-1">
            <button
              onClick={download}
              className="p-1 hover:bg-gray-100 rounded transition-colors"
              title="Download as PNG"
            >
              <Download size={15} className="text-gray-400" />
            </button>
            <button
              onClick={onClose}
              className="p-1 hover:bg-gray-100 rounded transition-colors"
            >
              <X size={15} className="text-gray-400" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div
          ref={containerRef}
          className="p-6 flex flex-col items-center gap-4"
        >
          <InlineMoleculeImage
            smiles={smiles}
            graph={graph}
            bboxWidth={bboxWidth}
            bboxHeight={bboxHeight}
            className="w-64 h-48"
          />
          <div className="w-full space-y-2">
            {coref && formatMetadata(coref) && (
              <div className="flex items-start gap-2">
                <span className="text-xs font-semibold text-slate-500 w-20 shrink-0 pt-0.5">
                  Metadata
                </span>
                <span className="text-xs text-slate-700">
                  {formatMetadata(coref)}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Reaction detail modal ─────────────────────────────────────────────────

const ReactionDetailModal: React.FC<{
  reactants: {
    smiles: string;
    uuid?: string;
    graph?: { symbols: string[]; coords: number[][]; edges: number[][] } | null;
    bboxWidth?: number;
    bboxHeight?: number;
  }[];
  products: {
    smiles: string;
    uuid?: string;
    graph?: { symbols: string[]; coords: number[][]; edges: number[][] } | null;
    bboxWidth?: number;
    bboxHeight?: number;
  }[];
  conditions: string[];
  onClose: () => void;
}> = ({ reactants, products, conditions, onClose }) => {
  const { containerRef, download } = useDownloadPng("reaction.png", "stitch");

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-2xl max-w-3xl w-full mx-4 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-100 bg-white">
          <h3 className="text-sm font-semibold text-gray-900">
            Reaction Detail
          </h3>
          <div className="flex items-center gap-1">
            <button
              onClick={download}
              className="p-1 hover:bg-gray-100 rounded transition-colors"
              title="Download as PNG"
            >
              <Download size={15} className="text-gray-400" />
            </button>
            <button
              onClick={onClose}
              className="p-1 hover:bg-gray-100 rounded transition-colors"
            >
              <X size={15} className="text-gray-400" />
            </button>
          </div>
        </div>

        {/* Reaction diagram */}
        <div ref={containerRef} className="p-6 overflow-x-auto">
          <div className="flex items-center justify-center gap-3 min-w-0">
            {/* Reactants */}
            {reactants.map((mol, idx) => (
              <React.Fragment key={mol.uuid || `r-${idx}`}>
                {idx > 0 && (
                  <span className="text-xl font-bold text-slate-400 shrink-0">
                    +
                  </span>
                )}
                <div className="flex flex-col items-center gap-1 shrink-0">
                  <InlineMoleculeImage
                    smiles={mol.smiles}
                    graph={mol.graph}
                    bboxWidth={mol.bboxWidth}
                    bboxHeight={mol.bboxHeight}
                    className="w-28 h-24"
                  />
                </div>
              </React.Fragment>
            ))}

            {/* Arrow + conditions */}
            <div className="flex flex-col items-center gap-1 shrink-0 mx-2">
              {conditions.length > 0 && (
                <div className="flex flex-col items-center">
                  {conditions.map((c, ci) => (
                    <span
                      key={ci}
                      className="text-[10px] text-slate-500 leading-tight"
                    >
                      {c}
                    </span>
                  ))}
                </div>
              )}
              <ArrowRight size={28} className="text-slate-600" />
            </div>

            {/* Products */}
            {products.map((mol, idx) => (
              <React.Fragment key={mol.uuid || `p-${idx}`}>
                {idx > 0 && (
                  <span className="text-xl font-bold text-slate-400 shrink-0">
                    +
                  </span>
                )}
                <div className="flex flex-col items-center gap-1 shrink-0">
                  <InlineMoleculeImage
                    smiles={mol.smiles}
                    graph={mol.graph}
                    bboxWidth={mol.bboxWidth}
                    bboxHeight={mol.bboxHeight}
                    className="w-28 h-24"
                  />
                </div>
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Types ──────────────────────────────────────────────────────────────────

type ChemistrySubTab = "molecules" | "reactions" | "tables" | "text";

interface ObjectsDisplayProps {
  /** Document ID used to generate public share links. */
  documentId: string;
  /** All pages of positioned data (same data used for the positioned tab). */
  pagesData: { pageId: string; rawData: any }[];
  /** Whether the current user can copy/share text blocks. */
  canShareText?: boolean;
  /** Callback to highlight a molecule / reaction on the image panel. */
  onHoverObject?: (uuid: string | null) => void;
  /** UUID of the object currently hovered on the original page (for reverse highlight). */
  hoveredObjectUuid?: string | null;
  /** Scroll the original-document (left) panel to a given page index. */
  onScrollToPage?: (pageIndex: number) => void;
  /** Highlight a bbox region on the left image panel (used by Text tab). */
  onHoverBbox?: (overlay: { pageIndex: number; bbox: number[] } | null) => void;
  /** Admin-only: edit a manually-added molecule's SMILES. */
  onEditMolecule?: (
    pageIndex: number,
    moleculeUuid: string,
    currentSmiles: string,
  ) => void;
  /** Admin-only: delete a manually-added molecule. */
  onDeleteMolecule?: (pageIndex: number, moleculeUuid: string) => void;
  /** Admin-only: delete multiple molecules at once. */
  onBulkDeleteMolecules?: (
    items: { pageIndex: number; moleculeUuid: string }[],
  ) => void | Promise<void>;
  /** Admin-only: edit a manually-added reaction's SMILES. */
  onEditReaction?: (
    pageIndex: number,
    reactionUuid: string,
    currentRxnSmiles: string,
  ) => void;
  /** Admin-only: delete a manually-added reaction. */
  onDeleteReaction?: (pageIndex: number, reactionUuid: string) => void;
  /** Admin-only: delete multiple reactions at once. */
  onBulkDeleteReactions?: (
    items: { pageIndex: number; reactionUuid: string }[],
  ) => void | Promise<void>;
}

// ─── Main component ─────────────────────────────────────────────────────────

const ObjectsDisplay: React.FC<ObjectsDisplayProps> = ({
  documentId,
  pagesData,
  canShareText = true,
  onHoverObject,
  hoveredObjectUuid,
  onScrollToPage,
  onHoverBbox,
  onEditMolecule,
  onDeleteMolecule,
  onBulkDeleteMolecules,
  onEditReaction,
  onDeleteReaction,
  onBulkDeleteReactions,
}) => {
  const [subTab, setSubTab] = useState<ChemistrySubTab>("molecules");

  // ── Aggregate molecules across all pages ─────────────────────────────────
  const allMolecules = useMemo(() => {
    const mols: {
      smiles: string;
      uuid: string;
      coref: string | null;
      pageIndex: number;
      bbox?: number[];
      graph?: {
        symbols: string[];
        coords: number[][];
        edges: number[][];
      } | null;
      bboxWidth?: number | undefined;
      bboxHeight?: number | undefined;
      source?: string | undefined;
    }[] = [];

    pagesData.forEach(({ rawData }, pageIndex) => {
      if (!rawData) return;

      // Build a coref lookup for this page: molecule uuid → coref text
      const corefMap = new Map<string, string>();
      (rawData.corefs || []).forEach((c: any) => {
        const molUuid =
          typeof c.molecule === "string" ? c.molecule : c.molecule?.uuid;
        if (molUuid && c.corefText) {
          corefMap.set(molUuid, c.corefText);
        }
      });

      const textLines = rawData.textLines || [];
      const textBlocks = rawData.textBlocks || [];
      const resolveCoref = (
        uuid: string,
        bbox: number[] | undefined,
      ): string | null =>
        corefMap.get(uuid) ??
        inferMetadataFromContext(bbox, textLines, textBlocks);

      // Standalone molecules (cls === 'molecule')
      (rawData.molecules || [])
        .filter((m: any) => m.cls === "molecule")
        .forEach((m: any) => {
          mols.push({
            smiles: m.text || "",
            uuid: m.uuid,
            coref: resolveCoref(m.uuid, m.bbox),
            pageIndex,
            bbox: m.bbox,
            graph: m.graph ?? null,
            bboxWidth: m.bbox_width,
            bboxHeight: m.bbox_height,
            source: m.source,
          });
        });

      // Molecules inside reactions (reactants + products)
      (rawData.rxns || []).forEach((rxn: any) => {
        const seen = new Set<string>();
        [...(rxn.reactants || []), ...(rxn.products || [])].forEach(
          (comp: any) => {
            if (comp.uuid && !seen.has(comp.uuid)) {
              seen.add(comp.uuid);
              // Avoid duplicates with standalone list
              if (!mols.some((m) => m.uuid === comp.uuid)) {
                mols.push({
                  smiles: comp.smiles || "",
                  uuid: comp.uuid,
                  coref: resolveCoref(comp.uuid, comp.bbox),
                  pageIndex,
                  bbox: comp.bbox,
                  graph: comp.graph ?? null,
                  bboxWidth: comp.bboxWidth,
                  bboxHeight: comp.bboxHeight,
                });
              }
            }
          },
        );
      });
    });

    return spatialSort(mols);
  }, [pagesData]);

  // ── Aggregate reactions across all pages ─────────────────────────────────
  const allReactions = useMemo(() => {
    const reactions: {
      uuid: string;
      reactants: { smiles: string; uuid?: string }[];
      products: { smiles: string; uuid?: string }[];
      conditions: string[];
      conditionUuids: string[];
      pageIndex: number;
      bbox?: number[];
      source?: string;
    }[] = [];

    pagesData.forEach(({ rawData }, pageIndex) => {
      if (!rawData) return;

      // New structured format: reactions in rawData.rxns
      const rxnSources: any[] = [...(rawData.rxns || [])];

      // Legacy format: reactions sit in rawData.molecules with cls === "rxn"
      // but they lack structured reactants/products, so we still surface them
      // as empty-component rows so the tab shows something.
      if ((rawData.rxns || []).length === 0) {
        (rawData.molecules || [])
          .filter((m: any) => m.cls === "rxn")
          .forEach((m: any) => {
            if (!rxnSources.some((r) => r.uuid === m.uuid)) {
              rxnSources.push(m);
            }
          });
      }

      rxnSources.forEach((rxn: any) => {
        reactions.push({
          uuid: rxn.uuid,
          reactants: (rxn.reactants || []).map((r: any) => ({
            smiles: r.smiles || "",
            uuid: r.uuid,
            graph: r.graph || null,
            bboxWidth: r.bboxWidth,
            bboxHeight: r.bboxHeight,
          })),
          products: (rxn.products || []).map((p: any) => ({
            smiles: p.smiles || "",
            uuid: p.uuid,
            graph: p.graph || null,
            bboxWidth: p.bboxWidth,
            bboxHeight: p.bboxHeight,
          })),
          conditions: (rxn.conditions || [])
            .map((c: any) => c.resolvedText || "")
            .filter(Boolean),
          conditionUuids: (rxn.conditions || []).flatMap(
            (c: any) => c.resolvedTextUuids || [],
          ),
          pageIndex,
          bbox: rxn.bbox,
          source: rxn.source,
        });
      });
    });

    return spatialSort(reactions);
  }, [pagesData]);

  // ── Aggregate tables across all pages ────────────────────────────────────
  const allTables = useMemo(() => {
    const tables: {
      uuid: string;
      html: string | null;
      pageIndex: number;
      bbox?: number[];
    }[] = [];
    pagesData.forEach(({ rawData }, pageIndex) => {
      if (!rawData) return;
      (rawData.molecules || [])
        .filter((m: any) => m.cls === "table")
        .forEach((m: any) => {
          tables.push({
            uuid: m.uuid,
            html: m.data?.html ?? null,
            pageIndex,
            bbox: m.bbox,
          });
        });
    });
    return tables;
  }, [pagesData]);

  // ── Aggregate text blocks across all pages ───────────────────────────────
  // One card per identified text_block (the green-bordered blocks). Skip
  // blocks whose bbox falls inside a table bbox.
  const allTextBlocks = useMemo(() => {
    type Bbox = [number, number, number, number];
    const tableBboxesByPage = new Map<number, Bbox[]>();
    allTables.forEach((t) => {
      if (!t.bbox || t.bbox.length < 4) return;
      const bbox: Bbox = [t.bbox[0]!, t.bbox[1]!, t.bbox[2]!, t.bbox[3]!];
      const list = tableBboxesByPage.get(t.pageIndex) || [];
      list.push(bbox);
      tableBboxesByPage.set(t.pageIndex, list);
    });

    const isInsideAnyTable = (blockBbox: Bbox, tableBboxes: Bbox[]) => {
      if (!tableBboxes.length) return false;
      const [lx1, ly1, lx2, ly2] = blockBbox;
      const blockArea = Math.max(0, lx2 - lx1) * Math.max(0, ly2 - ly1);
      if (blockArea <= 0) return false;
      return tableBboxes.some(([tx1, ty1, tx2, ty2]) => {
        const ix1 = Math.max(lx1, tx1);
        const iy1 = Math.max(ly1, ty1);
        const ix2 = Math.min(lx2, tx2);
        const iy2 = Math.min(ly2, ty2);
        const iw = ix2 - ix1;
        const ih = iy2 - iy1;
        if (iw <= 0 || ih <= 0) return false;
        return (iw * ih) / blockArea >= 0.5;
      });
    };

    const blocks: {
      text: string;
      pageIndex: number;
      blockKind?: string;
      lineUuid: string;
      bbox: number[];
    }[] = [];
    pagesData.forEach(({ rawData }, pageIndex) => {
      const pageBlocks = rawData?.textBlocks as
        | { uuid: string; bbox: number[]; blockKind?: string; text: string }[]
        | undefined;
      if (!pageBlocks?.length) return;
      const tableBboxes = tableBboxesByPage.get(pageIndex) || [];

      const pageOut: typeof blocks = [];
      pageBlocks.forEach((block) => {
        if (!block.bbox || block.bbox.length < 4) return;
        // Only surface prose-style blocks; drop annotations, margin text, and
        // miscellaneous (figure captions, equations, list items, stray text).
        if (
          block.blockKind &&
          !["header", "paragraph", "sentence"].includes(block.blockKind)
        )
          return;
        const bbox: Bbox = [
          block.bbox[0]!,
          block.bbox[1]!,
          block.bbox[2]!,
          block.bbox[3]!,
        ];
        if (isInsideAnyTable(bbox, tableBboxes)) return;
        pageOut.push({
          text: (block.text || "").trim(),
          pageIndex,
          lineUuid: block.uuid,
          bbox: block.bbox,
          ...(block.blockKind !== undefined && { blockKind: block.blockKind }),
        });
      });

      // Two-column-aware sort: detect if blocks cluster into left/right columns
      // by finding the largest gap in center-x values. If the gap is >10% of
      // page width, treat as two-column and sort left-column then right-column
      // (each top-to-bottom), interleaving full-width blocks by y.
      if (pageOut.length > 1) {
        const pageWidth = Math.max(...pageOut.map((b) => b.bbox[2] ?? 0));
        const centerXs = pageOut
          .map((b, i) => ({ cx: ((b.bbox[0] ?? 0) + (b.bbox[2] ?? 0)) / 2, i }))
          .sort((a, b) => a.cx - b.cx);

        let maxGap = 0;
        let splitX = pageWidth / 2;
        for (let k = 1; k < centerXs.length; k++) {
          const gap = centerXs[k]!.cx - centerXs[k - 1]!.cx;
          if (gap > maxGap) {
            maxGap = gap;
            splitX = (centerXs[k]!.cx + centerXs[k - 1]!.cx) / 2;
          }
        }

        const isTwoColumn = pageWidth > 0 && maxGap > pageWidth * 0.1;

        if (isTwoColumn) {
          // Full-width blocks span both columns; interleave them by y
          const fullWidthThreshold = pageWidth * 0.6;
          const fullWidth = pageOut.filter(
            (b) => (b.bbox[2] ?? 0) - (b.bbox[0] ?? 0) >= fullWidthThreshold,
          );
          const narrow = pageOut.filter(
            (b) => (b.bbox[2] ?? 0) - (b.bbox[0] ?? 0) < fullWidthThreshold,
          );
          const leftCol = narrow.filter(
            (b) => ((b.bbox[0] ?? 0) + (b.bbox[2] ?? 0)) / 2 < splitX,
          );
          const rightCol = narrow.filter(
            (b) => ((b.bbox[0] ?? 0) + (b.bbox[2] ?? 0)) / 2 >= splitX,
          );

          leftCol.sort((a, b) => (a.bbox[1] ?? 0) - (b.bbox[1] ?? 0));
          rightCol.sort((a, b) => (a.bbox[1] ?? 0) - (b.bbox[1] ?? 0));
          fullWidth.sort((a, b) => (a.bbox[1] ?? 0) - (b.bbox[1] ?? 0));

          // Merge: full-width blocks act as dividers ordered by y
          const columnOrder = [...leftCol, ...rightCol];
          const merged: typeof blocks = [];
          let fwIdx = 0;
          for (const block of columnOrder) {
            while (
              fwIdx < fullWidth.length &&
              (fullWidth[fwIdx]!.bbox[1] ?? 0) < (block.bbox[1] ?? 0)
            ) {
              merged.push(fullWidth[fwIdx++]!);
            }
            merged.push(block);
          }
          while (fwIdx < fullWidth.length) merged.push(fullWidth[fwIdx++]!);
          blocks.push(...merged);
        } else {
          pageOut.sort((a, b) => {
            const ay = a.bbox[1] ?? 0;
            const by = b.bbox[1] ?? 0;
            if (ay !== by) return ay - by;
            return (a.bbox[0] ?? 0) - (b.bbox[0] ?? 0);
          });
          blocks.push(...pageOut);
        }
      } else {
        blocks.push(...pageOut);
      }
    });
    return blocks;
  }, [pagesData, allTables]);

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="flex flex-col">
      {/* Sub‑tab bar */}
      <div className="sticky top-0 z-10 flex items-center gap-1 px-4 py-2 bg-white border-b border-gray-100">
        <button
          onClick={() => setSubTab("molecules")}
          className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
            subTab === "molecules"
              ? "bg-gray-100 text-gray-900"
              : "text-gray-500 hover:bg-gray-50 hover:text-gray-700"
          }`}
        >
          Molecules
        </button>
        <button
          onClick={() => setSubTab("reactions")}
          className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
            subTab === "reactions"
              ? "bg-gray-100 text-gray-900"
              : "text-gray-500 hover:bg-gray-50 hover:text-gray-700"
          }`}
        >
          Reactions
        </button>
        <button
          onClick={() => setSubTab("tables")}
          className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
            subTab === "tables"
              ? "bg-gray-100 text-gray-900"
              : "text-gray-500 hover:bg-gray-50 hover:text-gray-700"
          }`}
        >
          Tables
        </button>
        <button
          onClick={() => setSubTab("text")}
          className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
            subTab === "text"
              ? "bg-gray-100 text-gray-900"
              : "text-gray-500 hover:bg-gray-50 hover:text-gray-700"
          }`}
        >
          Text
        </button>
      </div>

      {/* Tab content */}
      <div>
        {subTab === "molecules" ? (
          <MoleculesTable
            molecules={allMolecules}
            onHoverObject={onHoverObject}
            hoveredObjectUuid={hoveredObjectUuid}
            onScrollToPage={onScrollToPage}
            {...(onEditMolecule && { onEditMolecule })}
            {...(onDeleteMolecule && { onDeleteMolecule })}
            {...(onBulkDeleteMolecules && { onBulkDeleteMolecules })}
          />
        ) : subTab === "reactions" ? (
          <ReactionsTable
            reactions={allReactions}
            onHoverObject={onHoverObject}
            hoveredObjectUuid={hoveredObjectUuid}
            onScrollToPage={onScrollToPage}
            {...(onEditReaction && { onEditReaction })}
            {...(onDeleteReaction && { onDeleteReaction })}
            {...(onBulkDeleteReactions && { onBulkDeleteReactions })}
          />
        ) : subTab === "tables" ? (
          <TablesTab tables={allTables} onScrollToPage={onScrollToPage} />
        ) : (
          <TextTab
            documentId={documentId}
            blocks={allTextBlocks}
            canShareText={canShareText}
            {...(onHoverObject !== undefined && { onHoverObject })}
            {...(hoveredObjectUuid !== undefined && { hoveredObjectUuid })}
            {...(onHoverBbox !== undefined && { onHoverBbox })}
            {...(onScrollToPage !== undefined && { onScrollToPage })}
          />
        )}
      </div>
    </div>
  );
};

// ─── Molecules table ────────────────────────────────────────────────────────

const MoleculesTable: React.FC<{
  molecules: {
    smiles: string;
    uuid: string;
    coref: string | null;
    pageIndex: number;
    graph?:
      | { symbols: string[]; coords: number[][]; edges: number[][] }
      | null
      | undefined;
    bboxWidth?: number | undefined;
    bboxHeight?: number | undefined;
    source?: string | undefined;
  }[];
  onHoverObject?: ((uuid: string | null) => void) | undefined;
  hoveredObjectUuid?: string | null | undefined;
  onScrollToPage?: ((pageIndex: number) => void) | undefined;
  onEditMolecule?: (
    pageIndex: number,
    moleculeUuid: string,
    currentSmiles: string,
  ) => void;
  onDeleteMolecule?: (pageIndex: number, moleculeUuid: string) => void;
  onBulkDeleteMolecules?: (
    items: { pageIndex: number; moleculeUuid: string }[],
  ) => void | Promise<void>;
}> = ({
  molecules,
  onHoverObject,
  hoveredObjectUuid,
  onScrollToPage,
  onEditMolecule,
  onDeleteMolecule,
  onBulkDeleteMolecules,
}) => {
  const showActions = Boolean(onEditMolecule || onDeleteMolecule);
  const showSelect = Boolean(onBulkDeleteMolecules);
  const rowRefs = useRef<Map<string, HTMLTableRowElement>>(new Map());
  const [selectedMol, setSelectedMol] = useState<
    (typeof molecules)[number] | null
  >(null);
  const [selectedUuids, setSelectedUuids] = useState<Set<string>>(new Set());
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);

  const setRowRef = useCallback(
    (uuid: string, el: HTMLTableRowElement | null) => {
      if (el) rowRefs.current.set(uuid, el);
      else rowRefs.current.delete(uuid);
    },
    [],
  );

  useEffect(() => {
    if (!hoveredObjectUuid) return;
    const row = rowRefs.current.get(hoveredObjectUuid);
    if (row) {
      row.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [hoveredObjectUuid]);

  // Drop selections for molecules that no longer exist (e.g. after a delete /
  // refresh reloads the list).
  useEffect(() => {
    setSelectedUuids((prev) => {
      if (prev.size === 0) return prev;
      const present = new Set(molecules.map((m) => m.uuid));
      const next = new Set([...prev].filter((u) => present.has(u)));
      return next.size === prev.size ? prev : next;
    });
  }, [molecules]);

  const allSelected =
    molecules.length > 0 && selectedUuids.size === molecules.length;

  const toggleSelectAll = (checked: boolean) => {
    setSelectedUuids(checked ? new Set(molecules.map((m) => m.uuid)) : new Set());
  };

  const toggleSelectOne = (uuid: string, checked: boolean) => {
    setSelectedUuids((prev) => {
      const next = new Set(prev);
      if (checked) next.add(uuid);
      else next.delete(uuid);
      return next;
    });
  };

  const handleBulkDelete = async () => {
    if (!onBulkDeleteMolecules || selectedUuids.size === 0) return;
    const items = molecules
      .filter((m) => selectedUuids.has(m.uuid))
      .map((m) => ({ pageIndex: m.pageIndex, moleculeUuid: m.uuid }));
    setIsBulkDeleting(true);
    try {
      await onBulkDeleteMolecules(items);
      setSelectedUuids(new Set());
    } finally {
      setIsBulkDeleting(false);
    }
  };

  if (molecules.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-3 text-gray-400">
        <Atom size={48} strokeWidth={1.25} className="text-gray-300" />
        <span className="text-sm">No molecules detected</span>
      </div>
    );
  }

  return (
    <>
      {showSelect && selectedUuids.size > 0 && (
        <div className="sticky top-0 z-20 flex items-center justify-between gap-3 px-4 py-2 bg-[#1d365d]/5 border-b border-gray-100">
          <span className="text-xs font-medium text-gray-700">
            {selectedUuids.size} selected
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleBulkDelete}
              disabled={isBulkDeleting}
              className="flex items-center gap-1.5 px-3 py-1 text-xs font-medium text-white bg-red-600 rounded hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isBulkDeleting ? (
                <span className="w-3 h-3 border-2 border-white/40 border-t-white rounded-full animate-spin" />
              ) : (
                <Trash2 size={13} />
              )}
              Delete {selectedUuids.size}
            </button>
          </div>
        </div>
      )}
      <table
        className="w-full text-left text-sm border-collapse"
        style={{ tableLayout: "fixed" }}
      >
        <colgroup>
          {showSelect && <col style={{ width: "5%" }} />}
          <col style={{ width: "6%" }} />
          <col style={{ width: "28%" }} />
          <col style={{ width: showActions ? "46%" : "56%" }} />
          <col style={{ width: "10%" }} />
          {showActions && <col style={{ width: "10%" }} />}
        </colgroup>
        <thead className="sticky top-0 z-10 bg-white border-b border-gray-100">
          <tr>
            {showSelect && (
              <th className="px-3 py-2">
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={(e) => toggleSelectAll(e.target.checked)}
                  className="w-3.5 h-3.5 cursor-pointer accent-[#1d365d]"
                  aria-label="Select all molecules"
                />
              </th>
            )}
            <th className="px-3 py-2 text-[11px] font-medium text-gray-400 uppercase tracking-wider">
              #
            </th>
            <th className="px-3 py-2 text-[11px] font-medium text-gray-400 uppercase tracking-wider">
              Image
            </th>
            <th className="px-3 py-2 text-[11px] font-medium text-gray-400 uppercase tracking-wider">
              Metadata
            </th>
            <th className="px-3 py-2 text-[11px] font-medium text-gray-400 uppercase tracking-wider">
              Page
            </th>
            {showActions && (
              <th className="px-3 py-2 text-[11px] font-medium text-gray-400 uppercase tracking-wider text-right">
                Actions
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {molecules.map((mol, idx) => {
            const isHighlighted = !!(
              hoveredObjectUuid && mol.uuid === hoveredObjectUuid
            );
            const isSelected = selectedUuids.has(mol.uuid);
            return (
              <tr
                key={mol.uuid || `mol-${idx}`}
                ref={(el) => setRowRef(mol.uuid, el)}
                data-page-index={mol.pageIndex}
                className={`border-b border-gray-100 transition-colors duration-100 cursor-pointer ${
                  isSelected
                    ? "bg-[#1d365d]/10"
                    : isHighlighted
                      ? "bg-[#1d365d]/5"
                      : "hover:bg-gray-50/70"
                }`}
                onMouseEnter={() => onHoverObject?.(mol.uuid)}
                onMouseLeave={() => onHoverObject?.(null)}
                onClick={() => {
                  onScrollToPage?.(mol.pageIndex);
                  setSelectedMol(mol);
                }}
              >
                {showSelect && (
                  <td
                    className="px-3 py-3"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={(e) =>
                        toggleSelectOne(mol.uuid, e.target.checked)
                      }
                      className="w-3.5 h-3.5 cursor-pointer accent-[#1d365d]"
                      aria-label={`Select molecule ${idx + 1}`}
                    />
                  </td>
                )}
                <td className="px-3 py-3 text-xs text-slate-400">{idx + 1}</td>
                <td className="px-3 py-3 overflow-hidden">
                  <InlineMoleculeImage
                    smiles={mol.smiles}
                    graph={mol.graph}
                    bboxWidth={mol.bboxWidth}
                    bboxHeight={mol.bboxHeight}
                  />
                </td>
                <td className="px-3 py-3 text-xs text-slate-600 overflow-hidden">
                  <span className="block truncate">
                    {formatMetadata(mol.coref) ?? (
                      <span className="text-slate-400 italic">None</span>
                    )}
                  </span>
                </td>
                <td className="px-3 py-3 text-xs text-slate-500">
                  {mol.pageIndex + 1}
                </td>
                {showActions && (
                  <td className="px-3 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      {onEditMolecule && mol.source === "manual" && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            onEditMolecule(
                              mol.pageIndex,
                              mol.uuid,
                              mol.smiles,
                            );
                          }}
                          className="p-1 rounded hover:bg-gray-200 transition-colors"
                          title="Edit SMILES"
                        >
                          <Pencil size={14} className="text-gray-600" />
                        </button>
                      )}
                      {onDeleteMolecule && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            onDeleteMolecule(mol.pageIndex, mol.uuid);
                          }}
                          className="p-1 rounded hover:bg-red-100 transition-colors"
                          title="Delete molecule"
                        >
                          <Trash2 size={14} className="text-red-600" />
                        </button>
                      )}
                    </div>
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>

      {selectedMol && (
        <MoleculeDetailModal
          smiles={selectedMol.smiles}
          coref={selectedMol.coref}
          graph={selectedMol.graph}
          bboxWidth={selectedMol.bboxWidth}
          bboxHeight={selectedMol.bboxHeight}
          onClose={() => setSelectedMol(null)}
        />
      )}
    </>
  );
};

// ─── Reactions table ────────────────────────────────────────────────────────

const ReactionsTable: React.FC<{
  reactions: {
    uuid: string;
    reactants: {
      smiles: string;
      uuid?: string;
      graph?: {
        symbols: string[];
        coords: number[][];
        edges: number[][];
      } | null;
      bboxWidth?: number;
      bboxHeight?: number;
    }[];
    products: {
      smiles: string;
      uuid?: string;
      graph?: {
        symbols: string[];
        coords: number[][];
        edges: number[][];
      } | null;
      bboxWidth?: number;
      bboxHeight?: number;
    }[];
    conditions: string[];
    conditionUuids: string[];
    pageIndex: number;
    source?: string | undefined;
  }[];
  onHoverObject?: ((uuid: string | null) => void) | undefined;
  hoveredObjectUuid?: string | null | undefined;
  onScrollToPage?: ((pageIndex: number) => void) | undefined;
  onEditReaction?: (
    pageIndex: number,
    reactionUuid: string,
    currentRxnSmiles: string,
  ) => void;
  onDeleteReaction?: (pageIndex: number, reactionUuid: string) => void;
  onBulkDeleteReactions?: (
    items: { pageIndex: number; reactionUuid: string }[],
  ) => void | Promise<void>;
}> = ({
  reactions,
  onHoverObject,
  hoveredObjectUuid,
  onScrollToPage,
  onEditReaction,
  onDeleteReaction,
  onBulkDeleteReactions,
}) => {
  const showActions = Boolean(onEditReaction || onDeleteReaction);
  const showSelect = Boolean(onBulkDeleteReactions);
  const rowRefs = useRef<Map<string, HTMLTableRowElement>>(new Map());
  const [selectedRxn, setSelectedRxn] = useState<
    (typeof reactions)[number] | null
  >(null);
  const [selectedUuids, setSelectedUuids] = useState<Set<string>>(new Set());
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);

  const setRowRef = useCallback(
    (uuid: string, el: HTMLTableRowElement | null) => {
      if (el) rowRefs.current.set(uuid, el);
      else rowRefs.current.delete(uuid);
    },
    [],
  );

  // Drop selections for reactions that no longer exist (e.g. after a delete /
  // refresh reloads the list).
  useEffect(() => {
    setSelectedUuids((prev) => {
      if (prev.size === 0) return prev;
      const present = new Set(reactions.map((r) => r.uuid));
      const next = new Set([...prev].filter((u) => present.has(u)));
      return next.size === prev.size ? prev : next;
    });
  }, [reactions]);

  const allSelected =
    reactions.length > 0 && selectedUuids.size === reactions.length;

  const toggleSelectAll = (checked: boolean) => {
    setSelectedUuids(checked ? new Set(reactions.map((r) => r.uuid)) : new Set());
  };

  const toggleSelectOne = (uuid: string, checked: boolean) => {
    setSelectedUuids((prev) => {
      const next = new Set(prev);
      if (checked) next.add(uuid);
      else next.delete(uuid);
      return next;
    });
  };

  const handleBulkDelete = async () => {
    if (!onBulkDeleteReactions || selectedUuids.size === 0) return;
    const items = reactions
      .filter((r) => selectedUuids.has(r.uuid))
      .map((r) => ({ pageIndex: r.pageIndex, reactionUuid: r.uuid }));
    setIsBulkDeleting(true);
    try {
      await onBulkDeleteReactions(items);
      setSelectedUuids(new Set());
    } finally {
      setIsBulkDeleting(false);
    }
  };

  // Build a lookup: any component uuid → reaction uuid, so we can scroll to the right row
  const componentToRxnUuid = useMemo(() => {
    const map = new Map<string, string>();
    reactions.forEach((rxn) => {
      map.set(rxn.uuid, rxn.uuid);
      rxn.reactants.forEach((r) => {
        if (r.uuid) map.set(r.uuid, rxn.uuid);
      });
      rxn.products.forEach((p) => {
        if (p.uuid) map.set(p.uuid, rxn.uuid);
      });
      rxn.conditionUuids.forEach((cu) => map.set(cu, rxn.uuid));
    });
    return map;
  }, [reactions]);

  useEffect(() => {
    if (!hoveredObjectUuid) return;
    const rxnUuid = componentToRxnUuid.get(hoveredObjectUuid);
    if (rxnUuid) {
      const row = rowRefs.current.get(rxnUuid);
      if (row) {
        row.scrollIntoView({ behavior: "smooth", block: "nearest" });
      }
    }
  }, [hoveredObjectUuid, componentToRxnUuid]);

  if (reactions.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-3 text-gray-400">
        <ArrowRightLeft size={48} strokeWidth={1.25} className="text-gray-300" />
        <span className="text-sm">No reactions detected</span>
      </div>
    );
  }

  return (
    <>
      {showSelect && selectedUuids.size > 0 && (
        <div className="sticky top-0 z-20 flex items-center justify-between gap-3 px-4 py-2 bg-[#1d365d]/5 border-b border-gray-100">
          <span className="text-xs font-medium text-gray-700">
            {selectedUuids.size} selected
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleBulkDelete}
              disabled={isBulkDeleting}
              className="flex items-center gap-1.5 px-3 py-1 text-xs font-medium text-white bg-red-600 rounded hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isBulkDeleting ? (
                <span className="w-3 h-3 border-2 border-white/40 border-t-white rounded-full animate-spin" />
              ) : (
                <Trash2 size={13} />
              )}
              Delete {selectedUuids.size}
            </button>
          </div>
        </div>
      )}
      <table
        className="w-full text-left text-sm border-collapse"
        style={{ tableLayout: "fixed" }}
      >
        <colgroup>
          {showSelect && <col style={{ width: "5%" }} />}
          <col style={{ width: "6%" }} />
          <col style={{ width: showActions ? "28%" : "30%" }} />
          <col style={{ width: showActions ? "28%" : "30%" }} />
          <col style={{ width: showActions ? "18%" : "24%" }} />
          <col style={{ width: "10%" }} />
          {showActions && <col style={{ width: "10%" }} />}
        </colgroup>
        <thead className="sticky top-0 z-10 bg-white border-b border-gray-100">
          <tr>
            {showSelect && (
              <th className="px-3 py-2">
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={(e) => toggleSelectAll(e.target.checked)}
                  className="w-3.5 h-3.5 cursor-pointer accent-[#1d365d]"
                  aria-label="Select all reactions"
                />
              </th>
            )}
            <th className="px-3 py-2 text-[11px] font-medium text-gray-400 uppercase tracking-wider">
              #
            </th>
            <th className="px-3 py-2 text-[11px] font-medium text-gray-400 uppercase tracking-wider">
              Reactants
            </th>
            <th className="px-3 py-2 text-[11px] font-medium text-gray-400 uppercase tracking-wider">
              Products
            </th>
            <th className="px-3 py-2 text-[11px] font-medium text-gray-400 uppercase tracking-wider">
              Conditions
            </th>
            <th className="px-3 py-2 text-[11px] font-medium text-gray-400 uppercase tracking-wider">
              Page
            </th>
            {showActions && (
              <th className="px-3 py-2 text-[11px] font-medium text-gray-400 uppercase tracking-wider text-right">
                Actions
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {reactions.map((rxn, idx) => {
            // Highlight if hovered uuid matches the reaction itself, any reactant, product, or condition text unit
            const isHighlighted = !!(
              hoveredObjectUuid &&
              (rxn.uuid === hoveredObjectUuid ||
                rxn.reactants.some((r) => r.uuid === hoveredObjectUuid) ||
                rxn.products.some((p) => p.uuid === hoveredObjectUuid) ||
                rxn.conditionUuids.includes(hoveredObjectUuid))
            );
            const isSelected = selectedUuids.has(rxn.uuid);
            return (
              <tr
                key={rxn.uuid || `rxn-${idx}`}
                ref={(el) => setRowRef(rxn.uuid, el)}
                data-page-index={rxn.pageIndex}
                className={`border-b border-gray-100 transition-colors duration-100 cursor-pointer align-top ${
                  isSelected
                    ? "bg-[#1d365d]/10"
                    : isHighlighted
                      ? "bg-[#1d365d]/5"
                      : "hover:bg-gray-50/70"
                }`}
                onMouseEnter={() => onHoverObject?.(rxn.uuid)}
                onMouseLeave={() => onHoverObject?.(null)}
                onClick={() => {
                  onScrollToPage?.(rxn.pageIndex);
                  setSelectedRxn(rxn);
                }}
              >
                {showSelect && (
                  <td
                    className="px-3 py-3"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={(e) =>
                        toggleSelectOne(rxn.uuid, e.target.checked)
                      }
                      className="w-3.5 h-3.5 cursor-pointer accent-[#1d365d]"
                      aria-label={`Select reaction ${idx + 1}`}
                    />
                  </td>
                )}
                <td className="px-3 py-3 text-xs text-slate-400">{idx + 1}</td>
                <td className="px-3 py-3 overflow-hidden">
                  <MoleculeStack molecules={rxn.reactants} />
                </td>
                <td className="px-3 py-3 overflow-hidden">
                  <MoleculeStack molecules={rxn.products} />
                </td>
                <td className="px-3 py-3 overflow-hidden">
                  {rxn.conditions.length > 0 ? (
                    <div className="flex flex-col gap-1 min-w-0">
                      {rxn.conditions.map((c, ci) => (
                        <span
                          key={ci}
                          className="text-xs text-slate-600 truncate block"
                          title={c}
                        >
                          {c}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <span className="text-xs text-slate-400 italic">None</span>
                  )}
                </td>
                <td className="px-3 py-3 text-xs text-slate-500">
                  {rxn.pageIndex + 1}
                </td>
                {showActions && (
                  <td className="px-3 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      {onEditReaction && rxn.source === "manual" && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            const reactantSmi = rxn.reactants
                              .map((r) => r.smiles)
                              .filter(Boolean)
                              .join(".");
                            const productSmi = rxn.products
                              .map((p) => p.smiles)
                              .filter(Boolean)
                              .join(".");
                            onEditReaction(
                              rxn.pageIndex,
                              rxn.uuid,
                              `${reactantSmi}>>${productSmi}`,
                            );
                          }}
                          className="p-1 rounded hover:bg-gray-200 transition-colors"
                          title="Edit reaction SMILES"
                        >
                          <Pencil size={14} className="text-gray-600" />
                        </button>
                      )}
                      {onDeleteReaction && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            onDeleteReaction(rxn.pageIndex, rxn.uuid);
                          }}
                          className="p-1 rounded hover:bg-red-100 transition-colors"
                          title="Delete reaction"
                        >
                          <Trash2 size={14} className="text-red-600" />
                        </button>
                      )}
                    </div>
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>

      {selectedRxn && (
        <ReactionDetailModal
          reactants={selectedRxn.reactants}
          products={selectedRxn.products}
          conditions={selectedRxn.conditions}
          onClose={() => setSelectedRxn(null)}
        />
      )}
    </>
  );
};

// ─── Tables tab ─────────────────────────────────────────────────────────────

const TablesTab: React.FC<{
  tables: {
    uuid: string;
    html: string | null;
    pageIndex: number;
    bbox?: number[];
  }[];
  onScrollToPage?: ((pageIndex: number) => void) | undefined;
}> = ({ tables, onScrollToPage }) => {
  if (tables.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-3 text-gray-400">
        <Table size={48} strokeWidth={1.25} className="text-gray-300" />
        <span className="text-sm">No tables detected</span>
      </div>
    );
  }

  return (
    <div className="divide-y divide-gray-100">
      <style>{`
        .table-explorer-container table { width: 100% !important; border-collapse: collapse !important; font-size: 12px !important; }
        .table-explorer-container th, .table-explorer-container td { border: 1px solid #cbd5e1 !important; padding: 4px 8px !important; text-align: left; }
        .table-explorer-container th { background-color: #f1f5f9 !important; font-weight: 600; }
        .table-explorer-container td { background-color: #ffffff !important; }
        .table-explorer-container tr:hover td { background-color: #f8fafc !important; }
      `}</style>
      {tables.map((tbl, idx) => (
        <div
          key={tbl.uuid}
          data-page-index={tbl.pageIndex}
          className="px-4 py-3"
        >
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-medium text-gray-700">
              Table {idx + 1}
            </span>
            <button
              className="text-xs text-blue-500 hover:text-blue-700"
              onClick={() => onScrollToPage?.(tbl.pageIndex)}
            >
              Page {tbl.pageIndex + 1}
            </button>
          </div>
          {tbl.html ? (
            <div className="overflow-auto rounded border border-slate-200">
              <div
                className="table-explorer-container"
                // LLM-generated HTML from a user-uploaded PDF — must be
                // sanitised before injection (#584).
                dangerouslySetInnerHTML={{ __html: sanitizeTableHtml(tbl.html) }}
              />
            </div>
          ) : (
            <div className="text-xs text-gray-400 italic">
              No content extracted
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

// ─── Text tab ────────────────────────────────────────────────────────────────

const TextTab: React.FC<{
  documentId: string;
  blocks: {
    text: string;
    pageIndex: number;
    blockKind?: string;
    lineUuid: string;
    bbox: number[];
  }[];
  canShareText?: boolean;
  onHoverObject?: (uuid: string | null) => void;
  hoveredObjectUuid?: string | null;
  onHoverBbox?: (overlay: { pageIndex: number; bbox: number[] } | null) => void;
  onScrollToPage?: (pageIndex: number) => void;
}> = ({ documentId, blocks, canShareText = true, onHoverObject, onHoverBbox, onScrollToPage }) => {
  const [pulsingIdx, setPulsingIdx] = useState<number | null>(null);
  const [copiedIdx, setCopiedIdx] = useState<number | null>(null);
  const [sharedIdx, setSharedIdx] = useState<number | null>(null);

  const handleCopy = async (e: React.MouseEvent, text: string, idx: number) => {
    e.stopPropagation();
    await navigator.clipboard.writeText(text);
    setCopiedIdx(idx);
    setTimeout(() => setCopiedIdx(null), 1500);
  };

  const handleShare = async (
    e: React.MouseEvent,
    block: { lineUuid: string; text: string },
    idx: number,
  ) => {
    e.stopPropagation();
    try {
      const { data } = await createPublicTextShare(
        documentId,
        block.lineUuid,
        block.text,
      );
      await navigator.clipboard.writeText(data.share_url);
      setSharedIdx(idx);
      setTimeout(() => setSharedIdx(null), 2000);
    } catch {
      // silently fail — could add a toast here
    }
  };

  if (blocks.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-sm text-gray-400">
        No text detected
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3 p-4">
      <style>{`
        @keyframes card-pulse {
          0%   { background-color: rgba(254,252,232,1); border-color: rgba(234,179,8,0.6); }
          50%  { background-color: rgba(253,224,71,0.4); border-color: rgba(234,179,8,1);  }
          100% { background-color: rgba(255,255,255,1); border-color: rgba(229,231,235,1); }
        }
      `}</style>
      {blocks.map((block, idx) => (
        <div
          key={idx}
          data-page-index={block.pageIndex}
          className="group relative border rounded-lg shadow-sm px-4 pt-3 pb-8 cursor-pointer hover:border-gray-300"
          style={
            pulsingIdx === idx
              ? {
                  animation: "card-pulse 1s ease-in-out 2 forwards",
                }
              : {
                  backgroundColor: "white",
                  borderColor: "rgb(229,231,235)",
                }
          }
          onClick={() => {
            onScrollToPage?.(block.pageIndex);
            setPulsingIdx(idx);
            onHoverObject?.(block.lineUuid);
            onHoverBbox?.({ pageIndex: block.pageIndex, bbox: block.bbox });
            setTimeout(() => {
              setPulsingIdx(null);
              onHoverObject?.(null);
              onHoverBbox?.(null);
            }, 2000);
          }}
        >
          {canShareText && (
            <div className="absolute bottom-2 right-2 flex items-center gap-0.5">
              <button
                onClick={(e) => handleShare(e, block, idx)}
                className="p-1 rounded hover:bg-gray-100"
                title="Copy link to this block"
              >
                {sharedIdx === idx ? (
                  <Check size={13} className="text-green-600" />
                ) : (
                  <Share size={13} className="text-gray-400" />
                )}
              </button>
              <button
                onClick={(e) => handleCopy(e, block.text, idx)}
                className="p-1 rounded hover:bg-gray-100"
                title="Copy text"
              >
                {copiedIdx === idx ? (
                  <Check size={13} className="text-green-600" />
                ) : (
                  <Copy size={13} className="text-gray-400" />
                )}
              </button>
            </div>
          )}
          <p className="text-xs text-gray-800 leading-relaxed">
            {block.text || (
              <span className="italic text-gray-400">(no text)</span>
            )}
          </p>
        </div>
      ))}
    </div>
  );
};

export default ObjectsDisplay;
